
import React, { useState, useEffect } from "react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  Star, 
  StarHalf,
  PlusCircle,
  Trash2,
  Crown,
  SaveIcon
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/hooks/use-user";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

interface Skill {
  id: string;
  skill_name: string;
  proficiency: number;
  is_primary: boolean;
}

interface UserSkillsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  canEdit: boolean;
}

const UserSkillsModal: React.FC<UserSkillsModalProps> = ({ 
  isOpen, 
  onClose,
  userId,
  canEdit 
}) => {
  const { user, refreshUser } = useUser();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newSkill, setNewSkill] = useState("");
  const [newProficiency, setNewProficiency] = useState(3);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchSkills = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from("user_skills")
          .select("*")
          .eq("user_id", userId)
          .order("is_primary", { ascending: false });
        
        if (error) {
          console.error("Error fetching skills:", error);
          throw error;
        }
        
        setSkills(data || []);
      } catch (error) {
        toast.error("Erro ao carregar skills");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSkills();
  }, [userId]);

  const handleAddSkill = async () => {
    if (!newSkill.trim()) return;
    
    setIsSaving(true);
    try {
      const newSkillObj: Omit<Skill, 'id'> & { user_id: string } = {
        user_id: userId,
        skill_name: newSkill,
        proficiency: newProficiency,
        is_primary: skills.length === 0 // First skill becomes primary
      };
      
      const { data, error } = await supabase
        .from("user_skills")
        .insert(newSkillObj)
        .select()
        .single();
        
      if (error) throw error;
      
      setSkills([...skills, data]);
      setNewSkill("");
      setNewProficiency(3);
      setIsAdding(false);
      
      if (newSkillObj.is_primary) {
        // Update profile with primary skill
        await supabase
          .from("profiles")
          .update({ primary_skill: newSkill })
          .eq("id", userId);
          
        if (user?.id === userId) {
          refreshUser();
        }
      }
      
      toast.success("Skill adicionada com sucesso");
    } catch (error) {
      toast.error("Erro ao adicionar skill");
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSkill = async (id: string) => {
    try {
      const skillToDelete = skills.find(s => s.id === id);
      const { error } = await supabase
        .from("user_skills")
        .delete()
        .eq("id", id);
        
      if (error) throw error;
      
      const updatedSkills = skills.filter(s => s.id !== id);
      setSkills(updatedSkills);
      
      // If primary skill was deleted, set another one as primary
      if (skillToDelete?.is_primary && updatedSkills.length > 0) {
        const { error: updateError } = await supabase
          .from("user_skills")
          .update({ is_primary: true })
          .eq("id", updatedSkills[0].id);
          
        if (!updateError) {
          const newPrimarySkill = updatedSkills[0].skill_name;
          await supabase
            .from("profiles")
            .update({ primary_skill: newPrimarySkill })
            .eq("id", userId);
            
          updatedSkills[0].is_primary = true;
          
          if (user?.id === userId) {
            refreshUser();
          }
        }
      }
      
      toast.success("Skill removida com sucesso");
    } catch (error) {
      toast.error("Erro ao remover skill");
      console.error(error);
    }
  };

  const handleSetPrimary = async (id: string) => {
    try {
      const skillToSetPrimary = skills.find(s => s.id === id);
      if (!skillToSetPrimary) return;
      
      // Update all skills to not primary
      await supabase
        .from("user_skills")
        .update({ is_primary: false })
        .eq("user_id", userId);
      
      // Set selected skill as primary
      const { error } = await supabase
        .from("user_skills")
        .update({ is_primary: true })
        .eq("id", id);
        
      if (error) throw error;
      
      // Update profile primary skill
      await supabase
        .from("profiles")
        .update({ primary_skill: skillToSetPrimary.skill_name })
        .eq("id", userId);
        
      // Update local state
      setSkills(skills.map(skill => ({
        ...skill,
        is_primary: skill.id === id
      })));
      
      if (user?.id === userId) {
        refreshUser();
      }
      
      toast.success("Skill principal atualizada com sucesso");
    } catch (error) {
      toast.error("Erro ao atualizar skill principal");
      console.error(error);
    }
  };

  const renderStars = (proficiency: number) => {
    const stars = [];
    const fullStars = Math.floor(proficiency);
    const hasHalfStar = proficiency % 1 !== 0;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={`full-${i}`} className="h-4 w-4 fill-primary text-primary" />);
    }
    
    if (hasHalfStar) {
      stars.push(<StarHalf key="half" className="h-4 w-4 fill-primary text-primary" />);
    }
    
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className="h-4 w-4 text-muted-foreground" />);
    }
    
    return stars;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Minhas Skills</DialogTitle>
        </DialogHeader>
        
        <div className="py-4 max-h-[50vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <div className="space-y-4">
              {skills.length === 0 && !isAdding && (
                <div className="text-center py-6 text-muted-foreground">
                  <p>Nenhuma skill cadastrada</p>
                </div>
              )}
              
              <AnimatePresence>
                {skills.map((skill) => (
                  <motion.div
                    key={skill.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      skill.is_primary ? 'border-primary/50 bg-primary/5' : 'border-border'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="font-medium">{skill.skill_name}</span>
                        {skill.is_primary && (
                          <Crown className="h-4 w-4 ml-1 text-amber-500" />
                        )}
                      </div>
                      <div className="flex items-center mt-1">
                        {renderStars(skill.proficiency)}
                      </div>
                    </div>
                    
                    {canEdit && (
                      <div className="flex space-x-2">
                        {!skill.is_primary && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-8 px-2"
                            onClick={() => handleSetPrimary(skill.id)}
                          >
                            <Crown className="h-4 w-4" />
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="destructive" 
                          className="h-8 px-2"
                          onClick={() => handleDeleteSkill(skill.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </motion.div>
                ))}
                
                {isAdding && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="border rounded-lg p-3"
                  >
                    <div className="space-y-2">
                      <Input
                        placeholder="Nome da skill"
                        value={newSkill}
                        onChange={(e) => setNewSkill(e.target.value)}
                      />
                      
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-muted-foreground">Proficiência:</span>
                        <div className="flex space-x-1">
                          {[1, 2, 3, 4, 5].map(level => (
                            <button
                              key={level}
                              type="button"
                              className="focus:outline-none"
                              onClick={() => setNewProficiency(level)}
                            >
                              <Star 
                                className={`h-5 w-5 ${
                                  level <= newProficiency 
                                    ? 'fill-primary text-primary' 
                                    : 'text-muted-foreground'
                                }`} 
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex justify-end space-x-2 pt-2">
                        <Button 
                          variant="outline"
                          onClick={() => setIsAdding(false)}
                          disabled={isSaving}
                        >
                          Cancelar
                        </Button>
                        <Button 
                          onClick={handleAddSkill}
                          disabled={!newSkill.trim() || isSaving}
                        >
                          {isSaving ? (
                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                          ) : (
                            <SaveIcon className="h-4 w-4 mr-2" />
                          )}
                          Salvar
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
        
        <DialogFooter>
          {canEdit && !isAdding && (
            <Button onClick={() => setIsAdding(true)} className="mr-auto">
              <PlusCircle className="h-4 w-4 mr-2" />
              Adicionar Skill
            </Button>
          )}
          <Button onClick={onClose} variant="outline">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UserSkillsModal;
